import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  UserPlus, Users, Clock, Calendar, Search,
  Shield, AlertTriangle, CheckCircle, XCircle, ClipboardList, Phone,
  Heart, Eye, Edit, Printer, Bell, LogIn, LogOut,
  TrendingUp, User, BarChart3, AlertCircle, Download, X
} from 'lucide-react';
import { useToast } from '../Common/ToastProvider';
import { ListSkeleton } from '../Common/SkeletonLoaders';
import { Tooltip } from '../Common/ReusableUI';

const TODAY = new Date().toISOString().slice(0, 10);
const NOW_HOURS = new Date().getHours();
const fmt = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const MOCK_RESIDENTS = [
  { id: 1, name: 'Margaret Rose Thompson', room: 'A101', ward: 'Banksia' },
  { id: 2, name: 'Arthur James Wilson', room: 'A102', ward: 'Banksia' },
  { id: 3, name: 'Dorothy May Chen', room: 'A103', ward: 'Banksia' },
  { id: 4, name: 'Harold William Nguyen', room: 'B201', ward: 'Wattle' },
  { id: 5, name: 'Edith Florence O\'Brien', room: 'B202', ward: 'Wattle' },
  { id: 6, name: 'George Henry Patel', room: 'B203', ward: 'Wattle' },
  { id: 7, name: 'Mavis Jean Kowalski', room: 'C301', ward: 'Grevillea' },
  { id: 8, name: 'Stanley Albert Murray', room: 'C302', ward: 'Grevillea' },
  { id: 9, name: 'Olive Grace Dimitriou', room: 'C303', ward: 'Grevillea' },
  { id: 10, name: 'Ronald Bruce Campbell', room: 'D401', ward: 'Waratah' },
  { id: 11, name: 'Phyllis Anne Mackenzie', room: 'D402', ward: 'Waratah' },
  { id: 12, name: 'Keith Raymond Singh', room: 'D403', ward: 'Waratah' },
];

const RELATIONSHIPS = ['Daughter', 'Son', 'Spouse', 'Friend', 'Volunteer', 'Grandchild', 'Niece', 'Nephew', 'Chaplain', 'Social Worker'];

const generateVisitorId = () => `VIS-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const INITIAL_VISITORS = [
  { id: 'VIS-001', name: 'Sarah Thompson', phone: '0412 345 678', relationship: 'Daughter', residentId: 1, date: TODAY, timeIn: fmt(NOW_HOURS - 2, 15), timeOut: null, screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-002', name: 'Michael Wilson', phone: '0423 456 789', relationship: 'Son', residentId: 2, date: TODAY, timeIn: fmt(NOW_HOURS - 1, 30), timeOut: null, screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-003', name: 'Linda Chen', phone: '0434 567 890', relationship: 'Daughter', residentId: 3, date: TODAY, timeIn: fmt(NOW_HOURS - 3, 0), timeOut: null, screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-004', name: 'James O\'Brien', phone: '0445 678 901', relationship: 'Son', residentId: 5, date: TODAY, timeIn: fmt(9, 30), timeOut: fmt(11, 45), screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-005', name: 'Priya Patel', phone: '0456 789 012', relationship: 'Grandchild', residentId: 6, date: TODAY, timeIn: fmt(10, 0), timeOut: fmt(12, 30), screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-006', name: 'Rev. David Hartley', phone: '0467 890 123', relationship: 'Chaplain', residentId: 7, date: TODAY, timeIn: fmt(NOW_HOURS - 1, 0), timeOut: null, screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-007', name: 'Karen Murray', phone: '0478 901 234', relationship: 'Spouse', residentId: 8, date: TODAY, timeIn: fmt(8, 0), timeOut: fmt(16, 0), screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-008', name: 'Angela Dimitriou', phone: '0489 012 345', relationship: 'Niece', residentId: 9, date: daysAgo(1), timeIn: '14:00', timeOut: '16:30', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-009', name: 'Tom Campbell', phone: '0490 123 456', relationship: 'Son', residentId: 10, date: daysAgo(1), timeIn: '10:00', timeOut: '12:15', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-010', name: 'Wendy Mackenzie', phone: '0401 234 567', relationship: 'Daughter', residentId: 11, date: daysAgo(1), timeIn: '09:00', timeOut: '11:30', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-011', name: 'Rajesh Singh', phone: '0412 876 543', relationship: 'Son', residentId: 12, date: daysAgo(2), timeIn: '13:00', timeOut: '15:45', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-012', name: 'Sarah Thompson', phone: '0412 345 678', relationship: 'Daughter', residentId: 1, date: daysAgo(2), timeIn: '10:00', timeOut: '13:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-013', name: 'Linda Chen', phone: '0434 567 890', relationship: 'Daughter', residentId: 3, date: daysAgo(3), timeIn: '14:30', timeOut: '16:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-014', name: 'Betty Kowalski', phone: '0423 765 432', relationship: 'Daughter', residentId: 7, date: daysAgo(3), timeIn: '11:00', timeOut: '14:30', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-015', name: 'Sam Nguyen', phone: '0434 654 321', relationship: 'Grandchild', residentId: 4, date: daysAgo(4), timeIn: '15:00', timeOut: '17:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-016', name: 'Jenny Wilson', phone: '0445 543 210', relationship: 'Daughter', residentId: 2, date: daysAgo(5), timeIn: '09:30', timeOut: '12:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-017', name: 'Maria Dimitriou', phone: '0456 432 109', relationship: 'Daughter', residentId: 9, date: daysAgo(6), timeIn: '10:30', timeOut: '13:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-018', name: 'Karen Murray', phone: '0478 901 234', relationship: 'Spouse', residentId: 8, date: daysAgo(1), timeIn: '08:00', timeOut: '16:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-019', name: 'Karen Murray', phone: '0478 901 234', relationship: 'Spouse', residentId: 8, date: daysAgo(2), timeIn: '08:00', timeOut: '16:00', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
  { id: 'VIS-020', name: 'Sandra O\'Brien', phone: '0467 321 098', relationship: 'Friend', residentId: 5, date: daysAgo(7), timeIn: '14:00', timeOut: '15:30', screening: { passed: true, answers: [false, false, false, false], flagged: false }, outbreakAnswers: [] },
];

const DEFAULT_SCREENING_QUESTIONS = [
  'Do you have any cold or flu symptoms?',
  'Have you had vomiting or diarrhoea in the last 48 hours?',
  'Have you been in contact with anyone with an infectious disease?',
  'Do you have any open wounds?',
];

const OUTBREAK_EXTRA_QUESTIONS = [
  'Have you tested positive for COVID-19 in the last 7 days?',
  'Have you been in a COVID-19 or influenza hotspot in the last 14 days?',
  'Have you received your latest influenza and COVID-19 vaccinations?',
];

const SCREENING_TIPS = [
  'Checks for respiratory symptoms that could spread to vulnerable residents.',
  'Gastro outbreaks can be deadly in aged care — even mild symptoms matter.',
  'Recent contact tracing helps prevent facility-wide exposure.',
  'Open wounds increase infection transmission risk for immunocompromised residents.',
];

const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const exportVisitorLogsCSV = (visitors, label) => {
  const headers = ['Date', 'Visitor', 'Phone', 'Relationship', 'Resident', 'Room', 'Time In', 'Time Out', 'Screening'];
  const rows = visitors.map(v => {
    const r = MOCK_RESIDENTS.find(res => res.id === v.residentId);
    return [v.date, v.name, v.phone, v.relationship, r?.name || '', r?.room || '', v.timeIn, v.timeOut || '', v.screening?.flagged ? 'Flagged' : 'Cleared'];
  });
  const csv = [
    headers.join(','),
    ...rows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  downloadCSV(csv, `visitor-log-${label}-${new Date().toISOString().slice(0, 10)}.csv`);
  return visitors.length;
};

const printBadge = (visitor) => {
  const resident = MOCK_RESIDENTS.find(r => r.id === visitor.residentId);
  const printWindow = window.open('', '_blank', 'width=450,height=650');
  if (!printWindow) return;
  printWindow.document.write(`<!DOCTYPE html>
<html>
<head><title>Visitor Badge — ${visitor.name}</title>
<style>
  @media print { @page { size: 3.5in 5in; margin: 0; } body { margin: 0; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #f3f4f6; }
  .badge { border: 3px solid #7c3aed; border-radius: 20px; padding: 28px 24px; width: 280px; background: #fff; text-align: center; box-shadow: 0 4px 24px rgba(0,0,0,.08); }
  .badge-header { color: #7c3aed; font-size: 13px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 16px; }
  .badge-photo { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, #e9d5ff, #c4b5fd); margin: 0 auto 14px; display: flex; align-items: center; justify-content: center; font-size: 28px; color: #7c3aed; }
  .badge-name { font-size: 22px; font-weight: 700; color: #1f2937; margin-bottom: 4px; }
  .badge-row { font-size: 13px; color: #6b7280; margin: 6px 0; }
  .badge-row strong { color: #374151; }
  .badge-divider { border: none; border-top: 1.5px solid #e5e7eb; margin: 16px 0; }
  .badge-id { font-size: 11px; color: #9ca3af; letter-spacing: 1px; }
  .badge-facility { font-size: 11px; color: #7c3aed; font-weight: 600; margin-top: 8px; }
</style>
</head>
<body onload="setTimeout(function(){window.print()},200)">
<div class="badge">
  <div class="badge-header">Visitor</div>
  <div class="badge-photo">${visitor.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
  <div class="badge-name">${visitor.name}</div>
  <div class="badge-row">Visiting: <strong>${resident?.name || 'Unknown'}</strong></div>
  <div class="badge-row">Room: <strong>${resident?.room || '—'}</strong> · ${resident?.ward || ''} Ward</div>
  <div class="badge-row">Date: <strong>${visitor.date}</strong> at <strong>${visitor.timeIn}</strong></div>
  <hr class="badge-divider" />
  <div class="badge-id">Badge #${visitor.id}</div>
  <div class="badge-facility">MedFast Aged Care</div>
</div>
</body>
</html>`);
  printWindow.document.close();
};

const Badge = ({ children, color = 'gray' }) => {
  const colors = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    purple: 'bg-purple-100 text-purple-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-100 text-gray-600',
    orange: 'bg-orange-100 text-orange-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>{children}</span>;
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'purple' }) => {
  const ring = { purple: 'bg-purple-50 text-purple-600', green: 'bg-green-50 text-green-600', red: 'bg-red-50 text-red-600', blue: 'bg-blue-50 text-blue-600', yellow: 'bg-yellow-50 text-yellow-600', orange: 'bg-orange-50 text-orange-600' };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${ring[color] || ring.purple}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const SignInOutTab = ({ visitors, setVisitors, outbreakMode, screeningQuestions }) => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', relationship: '', residentId: '', phone: '' });
  const [screeningStep, setScreeningStep] = useState(false);
  const [screeningAnswers, setScreeningAnswers] = useState([]);
  const [outbreakAnswers, setOutbreakAnswers] = useState([]);
  const [flagged, setFlagged] = useState(false);
  const [managerOverride, setManagerOverride] = useState(false);
  const [overridePin, setOverridePin] = useState('');
  const [searchActive, setSearchActive] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState(daysAgo(7));
  const [historyDateTo, setHistoryDateTo] = useState(TODAY);
  const [historySearch, setHistorySearch] = useState('');
  const [justSignedIn, setJustSignedIn] = useState(null);

  const activeVisitors = useMemo(() =>
    visitors.filter(v => v.date === TODAY && !v.timeOut)
      .filter(v => !searchActive || v.name.toLowerCase().includes(searchActive.toLowerCase()) || MOCK_RESIDENTS.find(r => r.id === v.residentId)?.name.toLowerCase().includes(searchActive.toLowerCase())),
    [visitors, searchActive]
  );

  const todayLog = useMemo(() => visitors.filter(v => v.date === TODAY && v.timeOut), [visitors]);

  const historyLog = useMemo(() =>
    visitors.filter(v => {
      if (v.date === TODAY) return false;
      if (v.date < historyDateFrom || v.date > historyDateTo) return false;
      if (historySearch) {
        const q = historySearch.toLowerCase();
        const resName = MOCK_RESIDENTS.find(r => r.id === v.residentId)?.name || '';
        return v.name.toLowerCase().includes(q) || resName.toLowerCase().includes(q);
      }
      return true;
    }).sort((a, b) => b.date.localeCompare(a.date) || b.timeIn.localeCompare(a.timeIn)),
    [visitors, historyDateFrom, historyDateTo, historySearch]
  );

  const resetForm = () => {
    setShowForm(false);
    setScreeningStep(false);
    setForm({ name: '', relationship: '', residentId: '', phone: '' });
    setScreeningAnswers([]);
    setOutbreakAnswers([]);
    setFlagged(false);
    setManagerOverride(false);
    setOverridePin('');
  };

  const handleStartScreening = () => {
    if (!form.name || !form.relationship || !form.residentId || !form.phone) return;
    setScreeningAnswers(new Array(screeningQuestions.length).fill(false));
    setOutbreakAnswers(outbreakMode ? new Array(OUTBREAK_EXTRA_QUESTIONS.length).fill(false) : []);
    setScreeningStep(true);
    setFlagged(false);
  };

  const toggleScreeningAnswer = (idx) => {
    setScreeningAnswers(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const toggleOutbreakAnswer = (idx) => {
    setOutbreakAnswers(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handleSubmitScreening = () => {
    const anyYes = screeningAnswers.some(Boolean) || outbreakAnswers.some(Boolean);
    if (anyYes && !managerOverride) {
      setFlagged(true);
      showToast('Screening flagged — manager override required for entry', 'warning');
      return;
    }
    const now = new Date();
    const newVisitor = {
      id: generateVisitorId(),
      name: form.name,
      phone: form.phone,
      relationship: form.relationship,
      residentId: Number(form.residentId),
      date: TODAY,
      timeIn: fmt(now.getHours(), now.getMinutes()),
      timeOut: null,
      screening: { passed: !anyYes || managerOverride, answers: [...screeningAnswers], flagged: anyYes },
      outbreakAnswers: [...outbreakAnswers],
    };
    setVisitors(prev => [...prev, newVisitor]);
    showToast(`${form.name} signed in successfully`, 'success');
    setJustSignedIn(newVisitor);
    resetForm();
  };

  const handleOverride = () => {
    if (overridePin === '1234') {
      setManagerOverride(true);
      setFlagged(false);
      showToast('Manager override approved — visitor may proceed with caution', 'warning');
    }
  };

  const signOut = (visitorId) => {
    const now = new Date();
    const visitor = visitors.find(v => v.id === visitorId);
    setVisitors(prev => prev.map(v => v.id === visitorId ? { ...v, timeOut: fmt(now.getHours(), now.getMinutes()) } : v));
    showToast(`${visitor?.name || 'Visitor'} signed out`, 'success');
  };

  const residentName = (id) => MOCK_RESIDENTS.find(r => r.id === id)?.name || 'Unknown';
  const residentRoom = (id) => MOCK_RESIDENTS.find(r => r.id === id)?.room || '';

  const duration = (tin, tout) => {
    if (!tin || !tout) return '—';
    const [h1, m1] = tin.split(':').map(Number);
    const [h2, m2] = tout.split(':').map(Number);
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff <= 0) return '—';
    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const handleExportHistory = () => {
    const count = exportVisitorLogsCSV(historyLog, 'history');
    showToast(`Exported ${count} visitor records to CSV`, 'success');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={LogIn} label="Currently In Facility" value={visitors.filter(v => v.date === TODAY && !v.timeOut).length} color="purple" />
        <StatCard icon={LogOut} label="Signed Out Today" value={todayLog.length} color="green" />
        <StatCard icon={Users} label="Total Visits Today" value={visitors.filter(v => v.date === TODAY).length} color="blue" />
        <StatCard icon={AlertTriangle} label="Flagged Today" value={visitors.filter(v => v.date === TODAY && v.screening?.flagged).length} color="red" />
      </div>

      {/* Print Badge prompt after sign-in */}
      {justSignedIn && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Printer size={20} className="text-purple-600" />
            <div>
              <p className="text-sm font-medium text-purple-900">{justSignedIn.name} signed in</p>
              <p className="text-xs text-purple-600">Print a visitor badge for identification.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { printBadge(justSignedIn); showToast('Badge sent to printer', 'info'); }} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition">
              <Printer size={14} /> Print Badge
            </button>
            <button onClick={() => setJustSignedIn(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><LogIn size={18} className="text-purple-600" /> Active Visitors</h3>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium">
            <UserPlus size={16} /> Sign In Visitor
          </button>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={searchActive} onChange={e => setSearchActive(e.target.value)} placeholder="Search active visitors or residents..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
        </div>

        {activeVisitors.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <Users size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No active visitors right now</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Visitor</th>
                  <th className="pb-2 font-medium">Visiting</th>
                  <th className="pb-2 font-medium">Room</th>
                  <th className="pb-2 font-medium">Relationship</th>
                  <th className="pb-2 font-medium">Time In</th>
                  <th className="pb-2 font-medium">Screening</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeVisitors.map(v => (
                  <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3">
                      <div className="font-medium text-gray-900">{v.name}</div>
                      <div className="text-xs text-gray-400 flex items-center gap-1"><Phone size={10} /> {v.phone}</div>
                    </td>
                    <td className="py-3 text-gray-700">{residentName(v.residentId)}</td>
                    <td className="py-3 text-gray-500">{residentRoom(v.residentId)}</td>
                    <td className="py-3"><Badge color="purple">{v.relationship}</Badge></td>
                    <td className="py-3 text-gray-700 flex items-center gap-1"><Clock size={13} className="text-gray-400" /> {v.timeIn}</td>
                    <td className="py-3">
                      {v.screening?.flagged
                        ? <Badge color="red">Flagged (Override)</Badge>
                        : <Badge color="green">Cleared</Badge>}
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => printBadge(v)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition text-xs font-medium" title="Print visitor badge">
                          <Printer size={13} />
                        </button>
                        <button onClick={() => signOut(v.id)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-xs font-medium">
                          <LogOut size={14} /> Sign Out
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {screeningStep ? <><Shield size={18} className="text-purple-600" /> Infection Screening</> : <><UserPlus size={18} className="text-purple-600" /> Visitor Sign-In</>}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><XCircle size={20} /></button>
              </div>

              {!screeningStep ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Visitor Name</label>
                    <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" placeholder="Full name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" placeholder="04XX XXX XXX" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Visiting Resident</label>
                    <select value={form.residentId} onChange={e => setForm(p => ({ ...p, residentId: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white">
                      <option value="">Select resident…</option>
                      {MOCK_RESIDENTS.map(r => <option key={r.id} value={r.id}>{r.name} — Room {r.room}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <select value={form.relationship} onChange={e => setForm(p => ({ ...p, relationship: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white">
                      <option value="">Select…</option>
                      {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time In</label>
                    <input readOnly value={fmt(new Date().getHours(), new Date().getMinutes())} className="w-full px-3 py-2 border border-gray-100 rounded-lg text-sm bg-gray-50 text-gray-600" />
                  </div>
                  <button onClick={handleStartScreening} disabled={!form.name || !form.relationship || !form.residentId || !form.phone} className="w-full py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition text-sm font-medium flex items-center justify-center gap-2">
                    <Shield size={16} /> Proceed to Screening
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {flagged && !managerOverride && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">Visitor Flagged — Infection Risk</p>
                          <p className="text-xs text-red-600 mt-1">One or more screening answers indicate potential risk. A manager override is required to allow entry.</p>
                          <div className="mt-3 flex items-center gap-2">
                            <input type="password" maxLength={4} value={overridePin} onChange={e => setOverridePin(e.target.value)} placeholder="Manager PIN" className="w-32 px-3 py-1.5 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300" />
                            <Tooltip content="Enter the facility manager PIN to approve entry despite flagged screening. This override is logged for audit purposes." position="right">
                              <button onClick={handleOverride} className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition">Override</button>
                            </Tooltip>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">Demo PIN: 1234</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {managerOverride && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2 text-sm text-yellow-800">
                      <CheckCircle size={16} className="text-yellow-600" /> Manager override approved. Visitor may proceed with caution.
                    </div>
                  )}

                  <p className="text-sm text-gray-600 font-medium">Standard Screening Questions</p>
                  {screeningQuestions.map((q, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                      <Tooltip content={SCREENING_TIPS[i] || 'Infection control screening question'} position="right">
                        <span className="text-sm text-gray-700 flex-1 cursor-help">{q}</span>
                      </Tooltip>
                      <div className="flex gap-1">
                        <button onClick={() => toggleScreeningAnswer(i)} className={`px-3 py-1 rounded text-xs font-medium transition ${screeningAnswers[i] ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}>Yes</button>
                        <button onClick={() => toggleScreeningAnswer(i)} className={`px-3 py-1 rounded text-xs font-medium transition ${!screeningAnswers[i] ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}>No</button>
                      </div>
                    </div>
                  ))}

                  {outbreakMode && (
                    <>
                      <p className="text-sm text-orange-600 font-medium flex items-center gap-1 mt-2"><AlertCircle size={14} /> Outbreak Mode — Additional Questions</p>
                      {OUTBREAK_EXTRA_QUESTIONS.map((q, i) => (
                        <div key={`ob-${i}`} className="flex items-start gap-3 p-3 rounded-lg border border-orange-100 bg-orange-50/30">
                          <span className="text-sm text-gray-700 flex-1">{q}</span>
                          <div className="flex gap-1">
                            <button onClick={() => toggleOutbreakAnswer(i)} className={`px-3 py-1 rounded text-xs font-medium transition ${outbreakAnswers[i] ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}>Yes</button>
                            <button onClick={() => toggleOutbreakAnswer(i)} className={`px-3 py-1 rounded text-xs font-medium transition ${!outbreakAnswers[i] ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}>No</button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  <div className="flex gap-3 mt-2">
                    <button onClick={() => { setScreeningStep(false); setFlagged(false); setManagerOverride(false); }} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Back</button>
                    <button onClick={handleSubmitScreening} className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium flex items-center justify-center gap-2">
                      <CheckCircle size={16} /> Complete Sign-In
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><Clock size={18} className="text-purple-600" /> Today's Visitor Log</h3>
        {todayLog.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No completed visits yet today</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Visitor</th>
                  <th className="pb-2 font-medium">Resident</th>
                  <th className="pb-2 font-medium">Time In</th>
                  <th className="pb-2 font-medium">Time Out</th>
                  <th className="pb-2 font-medium">Duration</th>
                  <th className="pb-2 font-medium">Screening</th>
                </tr>
              </thead>
              <tbody>
                {todayLog.map(v => (
                  <tr key={v.id} className="border-b border-gray-50">
                    <td className="py-2.5 font-medium text-gray-900">{v.name}</td>
                    <td className="py-2.5 text-gray-600">{residentName(v.residentId)}</td>
                    <td className="py-2.5 text-gray-600">{v.timeIn}</td>
                    <td className="py-2.5 text-gray-600">{v.timeOut}</td>
                    <td className="py-2.5 text-gray-600">{duration(v.timeIn, v.timeOut)}</td>
                    <td className="py-2.5">{v.screening?.flagged ? <Badge color="orange">Flagged</Badge> : <Badge color="green">Cleared</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Calendar size={18} className="text-purple-600" /> Historical Visitor Log</h3>
          <button onClick={handleExportHistory} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition text-xs font-medium" title="Export visitor history to CSV">
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>From</span>
            <input type="date" value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>To</span>
            <input type="date" value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="Search visitor or resident…" className="w-full pl-8 pr-4 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
          </div>
        </div>
        {historyLog.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No visits found for this period</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Visitor</th>
                  <th className="pb-2 font-medium">Resident</th>
                  <th className="pb-2 font-medium">Relationship</th>
                  <th className="pb-2 font-medium">Time In</th>
                  <th className="pb-2 font-medium">Time Out</th>
                  <th className="pb-2 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody>
                {historyLog.map(v => (
                  <tr key={v.id} className="border-b border-gray-50">
                    <td className="py-2.5 text-gray-600">{v.date}</td>
                    <td className="py-2.5 font-medium text-gray-900">{v.name}</td>
                    <td className="py-2.5 text-gray-600">{residentName(v.residentId)}</td>
                    <td className="py-2.5"><Badge color="purple">{v.relationship}</Badge></td>
                    <td className="py-2.5 text-gray-600">{v.timeIn}</td>
                    <td className="py-2.5 text-gray-600">{v.timeOut || '—'}</td>
                    <td className="py-2.5 text-gray-600">{duration(v.timeIn, v.timeOut)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ScreeningTab = ({ screeningQuestions, setScreeningQuestions, outbreakMode, setOutbreakMode }) => {
  const { showToast } = useToast();
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState('');
  const [newQuestion, setNewQuestion] = useState('');

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditText(screeningQuestions[idx]);
  };

  const saveEdit = () => {
    if (editText.trim()) {
      setScreeningQuestions(prev => prev.map((q, i) => i === editingIdx ? editText.trim() : q));
      showToast('Screening question updated', 'success');
    }
    setEditingIdx(null);
    setEditText('');
  };

  const removeQuestion = (idx) => {
    setScreeningQuestions(prev => prev.filter((_, i) => i !== idx));
    showToast('Screening question removed', 'info');
  };

  const addQuestion = () => {
    if (newQuestion.trim()) {
      setScreeningQuestions(prev => [...prev, newQuestion.trim()]);
      setNewQuestion('');
      showToast('Screening question added', 'success');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={ClipboardList} label="Standard Questions" value={screeningQuestions.length} color="purple" />
        <StatCard icon={Shield} label="Outbreak Questions" value={outbreakMode ? OUTBREAK_EXTRA_QUESTIONS.length : 0} sub={outbreakMode ? 'Active' : 'Inactive'} color={outbreakMode ? 'orange' : 'gray'} />
        <StatCard icon={AlertTriangle} label="Total Active" value={screeningQuestions.length + (outbreakMode ? OUTBREAK_EXTRA_QUESTIONS.length : 0)} color="blue" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Shield size={18} className="text-purple-600" /> Outbreak Mode</h3>
          <Tooltip content="When active, visitors must answer additional COVID-19 and influenza screening questions before entry. All responses are logged for compliance reporting." position="left">
            <button
              onClick={() => {
                setOutbreakMode(prev => {
                  const next = !prev;
                  showToast(next ? 'Outbreak mode activated — additional screening enabled' : 'Outbreak mode deactivated', next ? 'warning' : 'info');
                  return next;
                });
              }}
              className={`relative w-14 h-7 rounded-full transition-colors ${outbreakMode ? 'bg-orange-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${outbreakMode ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
          </Tooltip>
        </div>
        {outbreakMode && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-orange-800 font-medium flex items-center gap-2"><AlertCircle size={16} /> Outbreak Mode is ACTIVE</p>
            <p className="text-xs text-orange-600 mt-1">Additional COVID/flu screening questions will be shown to all visitors during sign-in.</p>
          </div>
        )}
        {outbreakMode && (
          <div className="space-y-2 mt-3">
            {OUTBREAK_EXTRA_QUESTIONS.map((q, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-orange-50/50 rounded-lg border border-orange-100">
                <AlertCircle size={14} className="text-orange-500 shrink-0" />
                <span className="text-sm text-gray-700 flex-1">{q}</span>
                <Badge color="orange">Outbreak</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><ClipboardList size={18} className="text-purple-600" /> Standard Screening Questions</h3>
        <div className="space-y-2">
          {screeningQuestions.map((q, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/30 group">
              {editingIdx === i ? (
                <>
                  <input value={editText} onChange={e => setEditText(e.target.value)} className="flex-1 px-3 py-1.5 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" onKeyDown={e => e.key === 'Enter' && saveEdit()} />
                  <button onClick={saveEdit} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 transition">Save</button>
                  <button onClick={() => setEditingIdx(null)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition">Cancel</button>
                </>
              ) : (
                <>
                  <CheckCircle size={14} className="text-green-500 shrink-0" />
                  <Tooltip content={SCREENING_TIPS[i] || 'Infection control screening question'} position="right">
                    <span className="text-sm text-gray-700 flex-1 cursor-help">{q}</span>
                  </Tooltip>
                  <button onClick={() => startEdit(i)} className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-purple-600 transition"><Edit size={14} /></button>
                  <button onClick={() => removeQuestion(i)} className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 transition"><XCircle size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <input value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="Add a new screening question…" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" onKeyDown={e => e.key === 'Enter' && addQuestion()} />
          <button onClick={addQuestion} disabled={!newQuestion.trim()} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1">
            <UserPlus size={14} /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

const VisitRecordsTab = ({ visitors }) => {
  const { showToast } = useToast();
  const [selectedResident, setSelectedResident] = useState('');
  const [period, setPeriod] = useState('month');

  const periodDays = period === 'week' ? 7 : 30;
  const cutoff = daysAgo(periodDays);

  const periodVisits = useMemo(() => visitors.filter(v => v.date >= cutoff), [visitors, cutoff]);

  const residentVisitMap = useMemo(() => {
    const map = {};
    MOCK_RESIDENTS.forEach(r => { map[r.id] = []; });
    periodVisits.forEach(v => {
      if (map[v.residentId]) map[v.residentId].push(v);
    });
    return map;
  }, [periodVisits]);

  const totalVisits = periodVisits.length;
  const uniqueVisitors = useMemo(() => new Set(periodVisits.map(v => v.name)).size, [periodVisits]);

  const mostVisited = useMemo(() => {
    let maxId = null, maxCount = 0;
    Object.entries(residentVisitMap).forEach(([id, visits]) => {
      if (visits.length > maxCount) { maxCount = visits.length; maxId = Number(id); }
    });
    return maxId ? { name: MOCK_RESIDENTS.find(r => r.id === maxId)?.name, count: maxCount } : null;
  }, [residentVisitMap]);

  const noVisitResidents = useMemo(() =>
    MOCK_RESIDENTS.filter(r => (residentVisitMap[r.id] || []).length === 0),
    [residentVisitMap]
  );

  const frequentVisitors = useMemo(() => {
    const counts = {};
    periodVisits.forEach(v => {
      const key = `${v.name}||${v.residentId}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([key, count]) => {
        const [name, resId] = key.split('||');
        return { name, residentId: Number(resId), count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [periodVisits]);

  const selectedResidentVisits = useMemo(() => {
    if (!selectedResident) return [];
    return visitors
      .filter(v => v.residentId === Number(selectedResident))
      .sort((a, b) => b.date.localeCompare(a.date) || b.timeIn.localeCompare(a.timeIn));
  }, [visitors, selectedResident]);

  const duration = (tin, tout) => {
    if (!tin || !tout) return '—';
    const [h1, m1] = tin.split(':').map(Number);
    const [h2, m2] = tout.split(':').map(Number);
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff <= 0) return '—';
    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const residentName = (id) => MOCK_RESIDENTS.find(r => r.id === id)?.name || 'Unknown';

  const weeklyBuckets = useMemo(() => {
    const buckets = Array.from({ length: 7 }, (_, i) => ({ label: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i], count: 0 }));
    periodVisits.forEach(v => {
      const dow = new Date(v.date).getDay();
      buckets[dow].count++;
    });
    return buckets;
  }, [periodVisits]);

  const maxBucket = Math.max(...weeklyBuckets.map(b => b.count), 1);

  const handleExportAll = () => {
    const count = exportVisitorLogsCSV(visitors, 'all-records');
    showToast(`Exported ${count} visit records to CSV`, 'success');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">Period:</span>
          {['week', 'month'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${period === p ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              This {p === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
        <button onClick={handleExportAll} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition text-xs font-medium">
          <Download size={14} /> Export All Records
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label={`Total Visits (${period})`} value={totalVisits} color="purple" />
        <StatCard icon={User} label="Unique Visitors" value={uniqueVisitors} color="blue" />
        <StatCard icon={Heart} label="Most Visited Resident" value={mostVisited?.count || 0} sub={mostVisited?.name || '—'} color="green" />
        <Tooltip content="Residents with zero visits this period may need a wellbeing check. Social isolation is a key indicator for mental health decline in aged care." position="bottom">
          <StatCard icon={AlertTriangle} label="No Visits (Wellbeing Flag)" value={noVisitResidents.length} color={noVisitResidents.length > 0 ? 'red' : 'green'} />
        </Tooltip>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-purple-600" /> Visit Frequency by Day</h3>
          <div className="flex items-end gap-3 h-40">
            {weeklyBuckets.map((b, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-gray-700">{b.count}</span>
                <div className="w-full rounded-t-md bg-purple-500 transition-all" style={{ height: `${(b.count / maxBucket) * 100}%`, minHeight: b.count > 0 ? 8 : 2 }} />
                <span className="text-xs text-gray-500">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-purple-600" /> Most Frequent Visitors</h3>
          {frequentVisitors.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No visit data for this period</p>
          ) : (
            <div className="space-y-3">
              {frequentVisitors.map((fv, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{fv.name}</p>
                    <p className="text-xs text-gray-500 truncate">Visiting {residentName(fv.residentId)}</p>
                  </div>
                  <Badge color="purple">{fv.count} visit{fv.count !== 1 ? 's' : ''}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {noVisitResidents.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="text-base font-semibold text-red-800 flex items-center gap-2 mb-3">
            <Tooltip content="Residents who have not received any visits in the selected period. Consider arranging a wellbeing check, volunteer visit, or contacting family." position="right">
              <span className="flex items-center gap-2 cursor-help">
                <AlertTriangle size={16} /> Residents with No Visits — Wellbeing Check Required
              </span>
            </Tooltip>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {noVisitResidents.map(r => (
              <div key={r.id} className="bg-white rounded-lg border border-red-100 p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center"><User size={16} /></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-500">Room {r.room} · {r.ward}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><Eye size={18} className="text-purple-600" /> Per-Resident Visit History</h3>
        <select value={selectedResident} onChange={e => setSelectedResident(e.target.value)} className="w-full sm:w-80 px-3 py-2 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white">
          <option value="">Select a resident…</option>
          {MOCK_RESIDENTS.map(r => <option key={r.id} value={r.id}>{r.name} — Room {r.room}</option>)}
        </select>

        {selectedResident && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-purple-700">{selectedResidentVisits.length}</p>
                <p className="text-xs text-gray-500">All-time Visits</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-blue-700">{selectedResidentVisits.filter(v => v.date >= daysAgo(7)).length}</p>
                <p className="text-xs text-gray-500">This Week</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-700">{selectedResidentVisits.filter(v => v.date >= daysAgo(30)).length}</p>
                <p className="text-xs text-gray-500">This Month</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-yellow-700">{new Set(selectedResidentVisits.map(v => v.name)).size}</p>
                <p className="text-xs text-gray-500">Unique Visitors</p>
              </div>
            </div>

            {selectedResidentVisits.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No recorded visits for this resident</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">Date</th>
                      <th className="pb-2 font-medium">Visitor</th>
                      <th className="pb-2 font-medium">Relationship</th>
                      <th className="pb-2 font-medium">Time In</th>
                      <th className="pb-2 font-medium">Time Out</th>
                      <th className="pb-2 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResidentVisits.map(v => (
                      <tr key={v.id} className="border-b border-gray-50">
                        <td className="py-2.5 text-gray-600">{v.date}</td>
                        <td className="py-2.5 font-medium text-gray-900">{v.name}</td>
                        <td className="py-2.5"><Badge color="purple">{v.relationship}</Badge></td>
                        <td className="py-2.5 text-gray-600">{v.timeIn}</td>
                        <td className="py-2.5 text-gray-600">{v.timeOut || '—'}</td>
                        <td className="py-2.5 text-gray-600">{duration(v.timeIn, v.timeOut)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ScheduleVisitTab = ({ scheduledVisits, setScheduledVisits }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({ visitorName: '', residentId: '', date: '', time: '', purpose: '' });

  const handleSchedule = (e) => {
    e.preventDefault();
    const resident = MOCK_RESIDENTS.find(r => r.id === Number(form.residentId));
    const visit = {
      id: `SCH-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
      visitorName: form.visitorName,
      residentId: Number(form.residentId),
      residentName: resident?.name || 'Unknown',
      date: form.date,
      time: form.time,
      purpose: form.purpose || 'General visit',
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    setScheduledVisits(prev => [...prev, visit]);
    setForm({ visitorName: '', residentId: '', date: '', time: '', purpose: '' });
    showToast(`Visit scheduled for ${form.visitorName} on ${form.date}`, 'success');
  };

  const cancelVisit = (id) => {
    setScheduledVisits(prev => prev.map(v => v.id === id ? { ...v, status: 'cancelled' } : v));
    showToast('Scheduled visit cancelled', 'info');
  };

  const confirmVisit = (id) => {
    setScheduledVisits(prev => prev.map(v => v.id === id ? { ...v, status: 'confirmed' } : v));
    showToast('Visit confirmed', 'success');
  };

  const upcomingVisits = useMemo(() =>
    scheduledVisits
      .filter(v => v.status !== 'cancelled' && v.date >= TODAY)
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)),
    [scheduledVisits]
  );

  const pastAndCancelledVisits = useMemo(() =>
    scheduledVisits
      .filter(v => v.date < TODAY || v.status === 'cancelled')
      .sort((a, b) => b.date.localeCompare(a.date)),
    [scheduledVisits]
  );

  const residentName = (id) => MOCK_RESIDENTS.find(r => r.id === id)?.name || 'Unknown';

  const statusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge color="yellow">Pending</Badge>;
      case 'confirmed': return <Badge color="green">Confirmed</Badge>;
      case 'cancelled': return <Badge color="red">Cancelled</Badge>;
      default: return <Badge color="gray">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Calendar} label="Upcoming Visits" value={upcomingVisits.length} color="purple" />
        <StatCard icon={CheckCircle} label="Confirmed" value={upcomingVisits.filter(v => v.status === 'confirmed').length} color="green" />
        <StatCard icon={Clock} label="Pending Confirmation" value={upcomingVisits.filter(v => v.status === 'pending').length} color="yellow" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Calendar size={18} className="text-purple-600" /> Schedule a Visit
        </h3>
        <form onSubmit={handleSchedule} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visitor Name</label>
            <input
              required
              value={form.visitorName}
              onChange={e => setForm(p => ({ ...p, visitorName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resident</label>
            <select
              required
              value={form.residentId}
              onChange={e => setForm(p => ({ ...p, residentId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
            >
              <option value="">Select resident…</option>
              {MOCK_RESIDENTS.map(r => <option key={r.id} value={r.id}>{r.name} — Room {r.room}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              required
              min={TODAY}
              value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <input
              type="time"
              required
              value={form.time}
              onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
            <input
              value={form.purpose}
              onChange={e => setForm(p => ({ ...p, purpose: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="e.g. Family visit, Doctor consultation, Chaplain visit"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={!form.visitorName || !form.residentId || !form.date || !form.time}
              className="w-full sm:w-auto px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition text-sm font-medium flex items-center justify-center gap-2"
            >
              <Calendar size={16} /> Schedule Visit
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Clock size={18} className="text-purple-600" /> Upcoming Scheduled Visits ({upcomingVisits.length})
        </h3>
        {upcomingVisits.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <Calendar size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No upcoming visits scheduled</p>
            <p className="text-xs mt-1">Use the form above to pre-schedule a visit</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Time</th>
                  <th className="pb-2 font-medium">Visitor</th>
                  <th className="pb-2 font-medium">Resident</th>
                  <th className="pb-2 font-medium">Purpose</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcomingVisits.map(v => (
                  <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 text-gray-700">{v.date}</td>
                    <td className="py-2.5 text-gray-700">{v.time}</td>
                    <td className="py-2.5 font-medium text-gray-900">{v.visitorName}</td>
                    <td className="py-2.5 text-gray-600">{residentName(v.residentId)}</td>
                    <td className="py-2.5 text-gray-600">{v.purpose}</td>
                    <td className="py-2.5">{statusBadge(v.status)}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {v.status === 'pending' && (
                          <button onClick={() => confirmVisit(v.id)} className="px-2.5 py-1 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition text-xs font-medium">
                            Confirm
                          </button>
                        )}
                        <button onClick={() => cancelVisit(v.id)} className="px-2.5 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-xs font-medium">
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pastAndCancelledVisits.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4 text-gray-500">
            Past & Cancelled Scheduled Visits ({pastAndCancelledVisits.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Visitor</th>
                  <th className="pb-2 font-medium">Resident</th>
                  <th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {pastAndCancelledVisits.slice(0, 10).map(v => (
                  <tr key={v.id} className="border-b border-gray-50 text-gray-500">
                    <td className="py-2">{v.date} {v.time}</td>
                    <td className="py-2">{v.visitorName}</td>
                    <td className="py-2">{residentName(v.residentId)}</td>
                    <td className="py-2">{statusBadge(v.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: 'signin', label: 'Sign In/Out', icon: LogIn },
  { id: 'screening', label: 'Screening', icon: Shield },
  { id: 'records', label: 'Visit Records', icon: ClipboardList },
  { id: 'schedule', label: 'Schedule Visit', icon: Calendar },
];

const Visitors = () => {
  useAppContext();
  const [activeTab, setActiveTab] = useState('signin');
  const [visitors, setVisitors] = useState(INITIAL_VISITORS);
  const [screeningQuestions, setScreeningQuestions] = useState([...DEFAULT_SCREENING_QUESTIONS]);
  const [outbreakMode, setOutbreakMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scheduledVisits, setScheduledVisits] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('mf_scheduledVisits') || '[]');
    } catch { return []; }
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('mf_scheduledVisits', JSON.stringify(scheduledVisits));
    } catch (e) {
      console.error('[Visitors] Failed to persist scheduled visits:', e);
    }
  }, [scheduledVisits]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users size={24} className="text-purple-600" /> Visitor Management</h1>
            <p className="text-sm text-gray-500 mt-1">Loading visitor data...</p>
          </div>
        </div>
        <ListSkeleton count={8} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users size={24} className="text-purple-600" /> Visitor Management</h1>
          <p className="text-sm text-gray-500 mt-1">Digital sign-in, infection screening & visit tracking</p>
        </div>
        {outbreakMode && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-lg text-sm text-orange-800 font-medium">
            <Bell size={14} className="text-orange-600" /> Outbreak Mode Active
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'signin' && (
        <SignInOutTab
          visitors={visitors}
          setVisitors={setVisitors}
          outbreakMode={outbreakMode}
          screeningQuestions={screeningQuestions}
        />
      )}
      {activeTab === 'screening' && (
        <ScreeningTab
          screeningQuestions={screeningQuestions}
          setScreeningQuestions={setScreeningQuestions}
          outbreakMode={outbreakMode}
          setOutbreakMode={setOutbreakMode}
        />
      )}
      {activeTab === 'records' && (
        <VisitRecordsTab visitors={visitors} />
      )}
      {activeTab === 'schedule' && (
        <ScheduleVisitTab
          scheduledVisits={scheduledVisits}
          setScheduledVisits={setScheduledVisits}
        />
      )}
    </div>
  );
};

export default Visitors;
